import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import {
	createMessage,
	getChatHistory,
	sendChatMessage,
	toChatMessages,
} from '../../../entities/chat-message';
import { formatErrorDetail } from '../../../shared/api/apiError';

export const useChat = ({ onError }) => {
	const intl = useIntl();
	const [messages, setMessages] = useState([]);
	const [isLoadingHistory, setIsLoadingHistory] = useState(true);
	const [isSending, setIsSending] = useState(false);

	const createWelcomeMessage = () =>
		createMessage(
			'assistant',
			intl.formatMessage({ id: 'AIAdvisorIDMessage5' }),
		);

	useEffect(() => {
		const loadHistory = async () => {
			try {
				const history = await getChatHistory();
				setMessages(
					history?.messages?.length
						? toChatMessages(history.messages)
						: [createWelcomeMessage()],
				);
			} catch (error) {
				console.error('Failed to load chat history:', error);
				setMessages([createWelcomeMessage()]);
			} finally {
				setIsLoadingHistory(false);
			}
		};
		loadHistory();
	}, []);

	// Resolves true when the reply arrived, so the input can restore the text
	// the user typed if sending failed.
	const sendMessage = async rawText => {
		const text = rawText.trim();
		if (!text || isSending) return false;

		const userMessage = createMessage('user', text);
		setMessages(prev => [...prev, userMessage]);
		setIsSending(true);

		try {
			const response = await sendChatMessage(text);
			if (!response) throw new Error();
			setMessages(prev => [
				...prev,
				createMessage('assistant', response.response),
			]);
			return true;
		} catch (error) {
			console.error('Error sending message:', error);
			setMessages(prev => prev.filter(m => m.id !== userMessage.id));

			onError(
				formatErrorDetail(
					error.response?.data?.detail,
					error.message || intl.formatMessage({ id: 'AIAdvisorIDMessage6' }),
				),
			);
			return false;
		} finally {
			setIsSending(false);
		}
	};

	const resetChat = () => setMessages([createWelcomeMessage()]);

	return { messages, isLoadingHistory, isSending, sendMessage, resetChat };
};
